import {Shift} from '@app/core/models/shift.model';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {User} from '@app/core/models/user.model';
import {BaseModel} from '@app/core/models/base.model';
import {Department} from '@app/core/models/department.model';
import {RDate} from '@app/core/models/rDate.model';
import {ShiftStockBid} from '@app/core/models/DatabaseSpecificModels/shiftStockBid.model';
import {ShiftStock} from '@app/core/models/DatabaseSpecificModels/shiftStock.model';
import {IAppState} from '@app/core/store/state/app.state';
import {Store} from '@ngrx/store';

export enum ActivitySubjectTypes {
	shift = 'App\\Models\\Shift',
	shiftStock = 'App\\Models\\ShiftStock',
	shiftStockBid = 'App\\Models\\ShiftStockBid',
}

export const SubjectToClass = {
	'App\\Models\\Shift': Shift,
	'App\\Models\\ShiftStock': ShiftStock,
	'App\\Models\\ShiftStockBid': ShiftStockBid,
};

export class Activity extends BaseModel {
	public id: number;
	public log_name: string | null;
	public description: string;
	public subject_id: number | null;
	public subject_type: ActivitySubjectTypes;
	public causer_id: number | null;
	public causer_type: string | null;
	public properties_cleaned: {
		attributes?: {},
		old?: {},
	} | null;
	public created_at: RDate;
	public updated_at: RDate;
	public causer: User | Department | null;
	public subject = BaseModel;

	public visible: string[] = [];
	public lines: { marked: boolean, text: string }[][];
	public store: Store<IAppState>;

	constructor(tenantTZ: string, interService: InternationalizationService, store?: Store<IAppState>) {
		super(tenantTZ, interService);
		this.store = store;
	}

	public deserialize(input: any): any {
		Object.assign(this, input);
		if (input.created_at) {
			this.created_at = new RDate(this.tenantTZ, this.interService).deserialize(input.created_at);
		}
		if (input.updated_at) {
			this.updated_at = new RDate(this.tenantTZ, this.interService).deserialize(input.updated_at);
		}
		if (input.causer && input.causer.first_name) {
			this.causer = new User(this.tenantTZ, this.interService).deserialize(input.causer);
		}
		if (input.causer && input.causer.department_name) {
			this.causer = new Department(this.tenantTZ, this.interService).deserialize(input.causer);
		}
		if (input.subject_type) {
			if (SubjectToClass[input.subject_type]) {
				this.subject = SubjectToClass[input.subject_type];
			}
			if (['created', 'updated'].indexOf(this.description) !== -1) {
				this.visible = this.visible.concat((this.description === 'created') ? this.subject.visibleCreated : this.subject.visibleUpdated);
			}
		}
		if (this.store) {
			this.lines = this.getLines(this.store);
		}

		return this;
	}

	public getLines(store: Store<IAppState>): { marked: boolean, text: string }[][] {
		const lines = [];
		this.visible.forEach(k => {
			if (this.visible.indexOf(k) === -1 || this.properties_cleaned.attributes[k] === undefined) {
				return;
			}
			const line = [];
			line.push({marked: false, text: this.interService.translate.instant('ACTIVITY_LOG.' + this.subject_type + '.' + k) + ' '});
			if (this.properties_cleaned.old && (this.properties_cleaned.old[k] || this.properties_cleaned.old[k] === 0)) {
				line.push({
					marked: false,
					text: this.interService.translate.instant('ACTIVITY_LOG.CHANGED') + ' ' + this.interService.translate.instant('ACTIVITY_LOG.FROM') + ' ',
				});
				line.push({
					marked: true,
					text: this.subject.formatAtt(this.tenantTZ, this.interService, store, k, this.properties_cleaned.old[k]) + ' ',
				});
			}
			if (this.properties_cleaned.attributes[k] || this.properties_cleaned.attributes[k] === 0) {
				line.push({marked: false, text: this.interService.translate.instant('ACTIVITY_LOG.TO') + ' '});
				line.push({
					marked: true,
					text: this.subject.formatAtt(this.tenantTZ, this.interService, store, k, this.properties_cleaned.attributes[k]),
				});
			}
			lines.push(line);
		});

		return lines;
	}

	public getTitle(): string {
		return this.subject.getTitle(this.tenantTZ, this.interService, this.description);
	}
}
